// ─────────────────────────────────────────────
//  ScheduleMeetingConstants.js
//  All static data & config for ScheduleMeeting
// ─────────────────────────────────────────────
import meetingService from '../../../../services/meetingService';

// ── Meeting types ─────────────────────────────
export const MEETING_TYPES = [
  // Backend-driven via configService in ScheduleMeeting.jsx
];

// ── Meeting durations ─────────────────────────
export const DURATIONS = [
  // Backend-driven via configService in ScheduleMeeting.jsx
];

// ── Time slots ────────────────────────────────
export const SLOTS = [
  // Backend-driven via configService in ScheduleMeeting.jsx
];

// ── Payment methods ─────────────────────────
export const PAYMENT_METHODS = [
  { id: "debit",      label: "Debit Card",  icon: "�", convFee: 0  },
  { id: "credit",     label: "Credit Card", icon: "💳", convFee: 18 },
  { id: "upi",        label: "UPI",         icon: "📱", convFee: 0  },
  { id: "netbanking", label: "Net Banking", icon: "🏦", convFee: 9  },
];

// ── GST rate ───────────────────────────────────
export const GST_RATE = 18;

// ── Days off (0 = Sunday, 6 = Saturday) ───────
export const DAYS_OFF = [0, 6];

// ── Max days in advance a booking can be made ─
export const MAX_ADVANCE_DAYS = 60;

// ── Stepper labels ────────────────────────────
export const STEPS = [
  "Meeting Types",
  "Meeting Duration",
  "Contact Details",
  "Review Your Booking",
  "Consent & Agreement",
  "Payment"
];

// ── Page Content ───────────────────────────────
export const PAGE_CONTENT = {
  header: {
    title: "Schedule a Meeting",
    subtitle: "Your Visa Journey Starts Here – Let’s Talk! Book Your consultation with our visa experts"
  },
  buttons: {
    continue: "Continue ",
    back: " Back",
    proceedToConsent: "Proceed ",
    pay: "Pay",
    bookAnother: "Book Another",
    backToHome: "Back to Home"
  },
  validation: {
    selectMeetingType: "Please select a meeting type.",
    selectDuration: "Please select a meeting duration.",
    selectDate: "Please choose a date.",
    weekendClosed: "We're closed on weekends.",
    selectSlot: "Please select a time slot.",
    nameRequired: "Full name is required.",
    validEmail: "Please enter a valid email address.",
    validPhone: "Please enter a valid phone number.",
    agreeToTerms: "Please agree to the terms to continue."
  },
  confirmation: {
    title: "Meeting Booked!",
    subtitle: "We'll send a confirmation to {email}."
  },
  footer: {
    needHelp: "Need help?"
  }
};

// ── Form labels & placeholders ────────────────
export const FORM_LABELS = {
  meetingDuration: "Meeting Duration",
  preferredDate: "Preferred Date",
  // dateHint: "(Mon \u2013 Fri only)",
  availableSlots: "Available Time Slots",
  fullName: "Full Name *",
  emailAddress: "Email Address *",
  phoneNumber: "Phone Number *",
  country: "Country",
  additionalNotes: "Additional Notes",
  optional: "(optional)",
};

export const PLACEHOLDERS = {
  fullName: "Your full name",
  email: "you@email.com",
  phone: "98765 43210",
  country: "Enter your country name",
  notes: "Anything specific you'd like us to prepare for\u2026",
  rewardAmount: "e.g. 200",
};

export const SUMMARY_LABELS = {
  service: "Service",
  duration: "Duration",
  date: "Date",
  time: "Time",
  name: "Name",
  email: "Email",
  phone: "Phone",
  country: "Country",
  total: "Total",
  meetingType: "Meeting Type",
  expert: "Expert",
  paymentMethod: "Payment Method",
  rewardDiscount: "Reward Discount",
  rewardUsed: "Reward Used",
  consultationFee: "Consultation Fee",
  convenienceFee: "Convenience Fee",
  gst: "GST ({rate}%)",
  totalPaid: "Total Paid",
  zeroViaRewards: "\u20b90 (Via Rewards)",
};

export const PRICE_LABELS = {
  consultationFee: "Consultation Fee",
  convenienceFee: "Convenience Fee",
  gst: "GST ({rate}%)",
  rewardDiscount: "Reward Discount",
  payByReward: "Pay by Reward",
  amountToPay: "Amount to Pay",
  total: "Total",
  free: "FREE",
};

export const REWARD_LABELS = {
  title: "Reward Discount",
  totalBalance: "Total Reward Balance:",
  maxDiscount: "Maximum Discount Allowed ({max}% of fee):",
  enterAmount: "Enter Reward Amount (\u20b9):",
  redeemableFirst: "Redeemable balance is used first, then non-redeemable.",
  clearPayByReward: 'Clear "Pay by Reward" to use reward discount.',
  rewards: "Rewards",
  payByReward: "Pay by Reward",
};

export const RECEIPT_LABELS = {
  scheduleMeeting: "Schedule Meeting",
  visaConsultation: "Visa Consultation",
  upi: "UPI",
  netBanking: "Net Banking",
  debitCreditCard: "Debit / Credit Card",
  payByReward: "Pay by Reward",
  filenamePrefix: "TheVisaCo-Receipt",
  paidByReward: "Paid by Reward",
};

export const SPEND_DESCRIPTIONS = {
  payByReward: "Schedule Meeting \u2014 Pay by Reward",
  rewardDiscount: "Schedule Meeting \u2014 Reward Discount",
};

export const IMAGE_ALT = {
  expert: "Expert",
};

export const MISC_LABELS = {
  downloadReceipt: "Download Receipt",
};

// ── Form defaults ─────────────────────────────
export const FORM_DEFAULTS = { name: "", email: "", phoneCode: "+91", phone: "", country: "", note: "" };

// ── Countries (for phone prefix) ─────────────
export const COUNTRIES = [
  { code: "+1", countryName: "United States", label: "US +1" },
  { code: "+44", countryName: "United Kingdom", label: "UK +44" },
  { code: "+91", countryName: "India", label: "IN +91" },
  { code: "+971", countryName: "United Arab Emirates", label: "UAE +971" },
  { code: "+65", countryName: "Singapore", label: "SG +65" },
  { code: "+61", countryName: "Australia", label: "AU +61" },
  { code: "+49", countryName: "Germany", label: "DE +49" },
  { code: "+33", countryName: "France", label: "FR +33" },
  { code: "+81", countryName: "Japan", label: "JP +81" },
  { code: "+86", countryName: "China", label: "CN +86" },
  { code: "+7", countryName: "Russia", label: "RU +7" },
  { code: "+55", countryName: "Brazil", label: "BR +55" },
  { code: "+82", countryName: "South Korea", label: "KR +82" },
  { code: "+39", countryName: "Italy", label: "IT +39" },
  { code: "+34", countryName: "Spain", label: "ES +34" },
  { code: "+31", countryName: "Netherlands", label: "NL +31" },
  { code: "+46", countryName: "Sweden", label: "SE +46" },
  { code: "+41", countryName: "Switzerland", label: "CH +41" },
  { code: "+352", countryName: "Luxembourg", label: "LU +352" },
  { code: "+353", countryName: "Ireland", label: "IE +353" },
];

// ── Expert profiles ───────────────────────────
export const EXPERTS = [
  {
    name:   "TheVisaCo. Team",
    title:  "Senior Visa Consultant",
    avatar: "TVC",
    specialization: ["visa-consult", "doc-review", "appeal", "interview-prep", "itinerary", "general"],
  },
  
];

// ── Company contact info ──────────────────────
export const COMPANY_INFO = {
  name:       "TheVisaCo",
  email:      "hello@thevisaco.com",
  phone:      "+91 98100 44015",
  whatsapp:   "https://wa.me/919810044015",
  address:    "New Delhi, India",
  // timezone:   "IST (UTC +5:30)",
  workingHours: "Mon – Fri, 9:30 AM-5:30 PM IST",
};

// ── Trust badges ──────────────────────────────
export const TRUST_BADGES = [
  // { icon: "🔒", label: "100% Confidential" },
  // { icon: "✅", label: "Free Consultation"  },
  // { icon: "⚡", label: "Quick Response"     },
  // { icon: "🌍", label: "Global Expertise"   },
];

// ── Helpers ───────────────────────────────────

/** Returns YYYY-MM-DD for tomorrow (min bookable date) */
export function getMinDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Returns YYYY-MM-DD for MAX_ADVANCE_DAYS from today */
export function getMaxDate() {
  const d = new Date();
  d.setDate(d.getDate() + MAX_ADVANCE_DAYS);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Returns true if the given YYYY-MM-DD string falls on a day off */
export function isDayOff(dateStr) {
  if (!dateStr) return false;
  const dow = new Date(dateStr + "T00:00:00").getDay();
  return DAYS_OFF.includes(dow);
}

/** Generates a unique booking ID */
export function genBookingId() {
  return `MTG-${Date.now().toString(36).toUpperCase()}`;
}

/** Converts "09:30 AM" → "09:30", "02:00 PM" → "14:00" */
function to24h(slot) {
  const trimmed = String(slot).trim();
  const m = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return trimmed;
  let h = +m[1];
  if (m[3].toUpperCase() === "PM" && h !== 12) h += 12;
  if (m[3].toUpperCase() === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

/** Persists a booking via API */
export async function saveBooking(booking, userEmail) {
  const email = userEmail || booking?.email;
  if (!email) throw new Error("No email provided for booking");

  // Extract known meta fields and pass through any extra dynamic contact fields
  const { id, type, typeLabel, duration, durationLabel,
    paymentMethod, paymentInfo, baseAmount, convFee, gstAmount, totalAmount,
    date, slot, expert, bookedAt, rewardDeduction, rewardDiscount,
    phoneCode, ...dynamicFields } = booking;

  await meetingService.book({
    meetingType: type,
    durationMinutes: parseInt(durationLabel || duration, 10) || 15,
    meetingDate: date,
    meetingSlot: to24h(slot || ""),
    name: booking.name,
    email: booking.email,
    phoneCode: phoneCode || "+91",
    phone: booking.phone,
    country: booking.country,
    notes: booking.note || booking.notes,
    paymentMethod: !paymentMethod || paymentMethod === "Free" ? undefined : (paymentMethod === "Pay by Reward" ? "reward" : paymentMethod),
    rewardPayBy: rewardDeduction || 0,
    rewardDiscount: rewardDiscount || 0,
    consented: true,
    // Pass through any extra dynamic contact detail fields
    ...dynamicFields,
  });

  return [booking];
}

/** Fetches bookings from API */
export async function fetchBookings(userEmail) {
  if (!userEmail) return [];
  try {
    const result = await meetingService.list();
    return (result.data || []).map(m => ({
      id: m.human_id || m.id,
      meetingType: m.meeting_type,
      duration: m.duration_minutes,
      date: m.date,
      slot: m.time_slot,
      name: m.name,
      email: m.email,
      phone: m.phone,
      bookedAt: m.created_at,
      status: m.status,
      totalAmount: m.total_amount,
    }));
  } catch {
    return [];
  }
}

/** Friendly date label: "Monday, 21 Apr 2025" */
export function formatDateLong(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "long",
    day:     "numeric",
    month:   "long",
    year:    "numeric",
  });
}

/** Short date: "21 Apr 2025" */
export function formatDateShort(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
    day:   "numeric",
    month: "short",
    year:  "numeric",
  });
}

/** Basic email validator */
export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Basic phone validator (7–15 digits, optional leading +) */
export function isValidPhone(phone) {
  return /^\+?[0-9\s\-().]{7,15}$/.test(phone.trim());
}

// ── Consent Form Content ────────────────────────

export const CONSENT_FORMS = {
  free: {
    title: "Consent Form – Free 15-Minute Google Meet Consultation",
    sections: [
      {
        title: "Purpose:",
        content: "This consent form is required before booking a free 15-minute consultation session via Google Meet.",
        type: "paragraph"
      },
      {
        title: "1. Service Overview",
        content: "I understand that I am booking a free consultation session of up to 15 minutes regarding visa-related services, conducted via Google Meet.",
        type: "paragraph"
      },
      {
        title: "2. Time Limitation",
        content: [
          "The session is strictly limited to 15 minutes.",
          "Any discussion beyond 15 minutes will require booking a paid consultation session."
        ],
        type: "list"
      },
      {
        title: "3. No Obligation",
        content: "I understand that this free session does not obligate me to purchase any further services.",
        type: "paragraph"
      },
      {
        title: "4. No Guarantee Clause",
        content: "I understand that this consultation does not guarantee visa approval or any specific outcome. The guidance provided is based on general information and professional experience.",
        type: "paragraph"
      },
      {
        title: "5. Meeting Guidelines",
        content: [
          "I will join the meeting on time using the provided Google Meet link.",
          "I will ensure a stable internet connection for the session.",
          "I will maintain professional and respectful communication.",
          "I understand that delays on my part may reduce my consultation time.",
          "The session may be ended if guidelines are not followed."
        ],
        type: "list"
      },
      {
        title: "6. Consent & Agreement",
        content: "By checking the box below, I confirm that:",
        listItems: [
          "I have read and understood the terms above.",
          "I agree to proceed with the free consultation."
        ],
        type: "agreement"
      }
    ],
    checkboxLabel: "I agree to the terms and conditions"
  },
  paid: {
    title: "Consent Form – Paid Consultation Session",
    sections: [
      {
        title: "1. Service Overview",
        content: "I understand that I am booking a paid consultation session exceeding 15 minutes regarding visa-related services, conducted via Google Meet.",
        type: "paragraph"
      },
      {
        title: "2. Session Duration & Charges",
        content: [
          "The first 15 minutes are free.",
          "Any consultation beyond 15 minutes will be chargeable as per the applicable rate.",
          "Charges will be clearly communicated before proceeding with the extended session."
        ],
        type: "list"
      },
      {
        title: "3. Payment Policy",
        content: [
          "I agree to make the required payment before continuing the session beyond 15 minutes.",
          "Payments once made are non-refundable, except in cases where the service is not delivered by the provider."
        ],
        type: "list"
      },
      {
        title: "4. User Responsibility",
        content: [
          "I will join the meeting on time using the provided link.",
          "I understand that delays on my part will not extend the session duration."
        ],
        type: "list"
      },
      {
        title: "5. No Guarantee Clause",
        content: "I understand that this consultation does not guarantee visa approval or any specific outcome. Advice is based on current rules and professional judgment.",
        type: "paragraph"
      },
      {
        title: "6. Meeting Guidelines",
        content: [
          "I will maintain professional behavior during the session.",
          "I will ensure a quiet environment and stable internet connection.",
          "I will not record the session without prior permission.",
          "The session may be terminated in case of misconduct or violation of guidelines."
        ],
        type: "list"
      },
      {
        title: "7. Consent & Agreement",
        content: "By checking the box below, I confirm that:",
        listItems: [
          "I have read and understood all the terms above.",
          "I agree to proceed with the paid consultation beyond 15 minutes."
        ],
        type: "agreement"
      }
    ],
    checkboxLabel: "I agree to the terms and conditions"
  }
};

/** Returns consent form based on duration */
export function getConsentForm(duration, durations, consentFree, consentPaid) {
  const list = durations || DURATIONS;
  const selected = list.find(d => d.id === duration);
  const isFree = selected?.price === "Free";

  // If dynamic consent data is provided from the backend, use it
  const dynamicSections = isFree ? consentFree : consentPaid;
  if (dynamicSections && dynamicSections.length > 0) {
    return {
      title: isFree ? CONSENT_FORMS.free.title : CONSENT_FORMS.paid.title,
      sections: dynamicSections,
      checkboxLabel: CONSENT_FORMS.free.checkboxLabel,
    };
  }

  // Fallback to hardcoded constants
  return isFree ? CONSENT_FORMS.free : CONSENT_FORMS.paid;
}

// ═══════════════════════════════════════════════════════════
//  REWARD SYSTEM CONSTANTS
// ═══════════════════════════════════════════════════════════

export const REWARD_CONSTANTS = {
  MAX_REWARD_PERCENTAGE: 20,
  PAYMENT_MODES: {
    REWARD_DISCOUNT: "reward_discount",
    PAY_BY_REWARD: "pay_by_reward",
  },
  REWARD_TYPES: {
    REDEEMABLE: "redeemable",
    NON_REDEEMABLE: "nonRedeemable",
  },
  VALIDATION_MESSAGES: {
    EXCEEDS_MAX_PERCENTAGE: "Reward discount cannot exceed {max}% of the total price.",
    EXCEEDS_BALANCE: "Reward discount cannot exceed your available redeemable balance of ₹{balance}.",
    INSUFFICIENT_BALANCE: "Insufficient reward balance. You need ₹{required} but have ₹{available}.",
    INVALID_AMOUNT: "Please enter a valid reward amount.",
    NEGATIVE_AMOUNT: "Reward amount cannot be negative.",
    SELECT_PAYMENT: "Please select a payment method.",
    EXCEEDS_TOTAL: "Reward discount cannot exceed the total payable amount.",
    NOT_LOGGED_IN: "Please sign in to use reward features.",
    PAYMENT_FAILED: "Payment processing failed. Please try again.",
  },
  LOGIN_MESSAGES: {
    SIGN_IN_TO_USE: "Sign In to Use Rewards",
    LOGIN_REQUIRED: "Please sign in to use reward features.",
    SIGN_IN_PROMPT: "Sign in to unlock your rewards and save on your booking.",
    SIGN_IN_TITLE: "Sign in to unlock rewards",
  },
  BUTTON_LABELS: {
    SIGN_IN: "Sign In to Use Rewards",
    LOGIN: "Sign In",
    SIGNUP: "Create Account",
    SWITCH_TO_LOGIN: "Already have an account? Sign In",
    SWITCH_TO_SIGNUP: "Don't have an account? Sign Up",
    CANCEL: "Cancel",
  },
  REWARD_LIMITS: {
    MIN_REWARD_AMOUNT: 1,
  },
  PAYMENT_STATUS: {
    PENDING: "pending",
    PAID: "paid",
    FAILED: "failed",
  },
  MODAL_TYPES: {
    LOGIN: "login",
    SIGNUP: "signup",
  },
};

